import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Megaphone, Send, UserPlus } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, StatCard, Badge } from "@/components/kit";

const PLANS = ["chef", "sous_chef", "executive_chef", "master_chef"];
const ROLES = ["admin", "master_chef", "executive_chef", "chef", "sous_chef"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(apiErr(e)));
    api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch(() => {});
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const updUser = async (id, patch) => { await api.put(`/admin/users/${id}`, patch); toast.success("User updated."); load(); };
  const updPlan = async (id, patch) => { await api.put(`/admin/plans/${id}`, patch); toast.success("Plan updated."); load(); };
  const [bc, setBc] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const broadcast = async () => {
    if (!bc.subject || !bc.message) return toast.error("Subject and message are required.");
    setSending(true);
    try { const { data } = await api.post("/admin/broadcast", bc); toast.success(data.message); setBc({ subject: "", message: "" }); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const [inv, setInv] = useState({ email: "", name: "", plan: "chef", role: "chef", custom_price: "" });
  const invite = async () => {
    if (!inv.email) return toast.error("Email is required.");
    try { const { data } = await api.post("/admin/invite", inv); toast.success(`${data.message} Temp password: ${data.temp_password}`); setInv({ email: "", name: "", plan: "chef", role: "chef", custom_price: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!stats) return <Loader />;
  return (
    <div>
      <PageHeader eyebrow="Platform" title="Admin Dashboard" subtitle="Manage users, subscriptions, plan pricing and platform analytics."
        action={<Badge tone="gold"><Shield size={13} className="mr-1.5" /> Admin</Badge>} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Users" value={stats.users} />
        <StatCard label="Menus" value={stats.menus} />
        <StatCard label="Recipes" value={stats.recipes} />
        <StatCard label="Paid Subs" value={stats.paid_subscriptions} />
        <StatCard label="Revenue" value={`$${stats.revenue.toLocaleString()}`} accent />
      </div>

      <div className="matte-card p-6 mb-6">
        <p className="label-eyebrow mb-4 flex items-center gap-2"><UserPlus size={14} className="text-gold" /> Invite a Chef</p>
        <p className="text-white/40 text-sm mb-4">Invite any chef on any plan, with an optional custom price.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} data-testid="invite-email" placeholder="Email" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
          <input value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} data-testid="invite-name" placeholder="Name" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
          <select value={inv.plan} onChange={(e) => setInv({ ...inv, plan: e.target.value })} data-testid="invite-plan" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60">{PLANS.map((p) => <option key={p} value={p}>{p.replace("_", " ")}</option>)}</select>
          <input type="number" value={inv.custom_price} onChange={(e) => setInv({ ...inv, custom_price: e.target.value })} data-testid="invite-price" placeholder="Custom $/mo (opt)" className="bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60" />
          <button onClick={invite} data-testid="invite-submit" className="btn-gold px-6 text-xs uppercase tracking-widest rounded-sm flex items-center justify-center gap-2"><Send size={14} /> Invite</button>
        </div>
      </div>

      <div className="matte-card p-6 mb-6">
        <p className="label-eyebrow mb-4 flex items-center gap-2"><Megaphone size={14} className="text-gold" /> Broadcast Announcement</p>
        <p className="text-white/40 text-sm mb-4">Send a branded email announcement to every chef on the platform.</p>
        <input value={bc.subject} onChange={(e) => setBc({ ...bc, subject: e.target.value })} data-testid="broadcast-subject" placeholder="Subject line"
          className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 mb-3 text-white/90 outline-none focus:border-gold/60" />
        <textarea rows={4} value={bc.message} onChange={(e) => setBc({ ...bc, message: e.target.value })} data-testid="broadcast-message" placeholder="Write your announcement…"
          className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 mb-3 text-white/90 outline-none focus:border-gold/60 resize-none" />
        <button onClick={broadcast} disabled={sending} data-testid="broadcast-send-btn" className="btn-gold px-6 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
          <Send size={14} /> {sending ? "Sending…" : "Send to All Chefs"}
        </button>
      </div>

      <div className="matte-card p-6 mb-6">
        <p className="label-eyebrow mb-4">Plan Pricing & Limits (editable)</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-eyebrow border-b border-white/10"><th className="py-2 pr-3">Plan</th><th className="py-2 px-3">Monthly $</th><th className="py-2 px-3">Yearly $</th><th className="py-2 px-3">Max Courses</th><th className="py-2 px-3">Gen Limit</th></tr></thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-white/5">
                  <td className="py-3 pr-3 font-serif text-lg">{p.name}</td>
                  <td className="py-3 px-3"><EditNum value={p.monthly} onSave={(v) => updPlan(p.id, { monthly: v })} testid={`plan-monthly-${p.id}`} /></td>
                  <td className="py-3 px-3"><EditNum value={p.yearly} onSave={(v) => updPlan(p.id, { yearly: v })} /></td>
                  <td className="py-3 px-3"><EditNum value={p.limits?.max_courses} onSave={(v) => updPlan(p.id, { limits: { ...p.limits, max_courses: v } })} /></td>
                  <td className="py-3 px-3"><EditNum value={p.limits?.menu_generations} onSave={(v) => updPlan(p.id, { limits: { ...p.limits, menu_generations: v } })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-white/30 text-xs mt-3">Note: display prices update immediately. New Stripe prices require a catalog re-sync on deploy.</p>
      </div>

      <div className="matte-card p-6">
        <p className="label-eyebrow mb-4">Users</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-eyebrow border-b border-white/10"><th className="py-2 pr-3">Name</th><th className="py-2 px-3">Email</th><th className="py-2 px-3">Role</th><th className="py-2 px-3">Plan</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-black/40">
                  <td className="py-3 pr-3 text-white/90">{u.name}</td>
                  <td className="py-3 px-3 text-white/50">{u.email}</td>
                  <td className="py-3 px-3">
                    <select value={u.role} onChange={(e) => updUser(u.id, { role: e.target.value })} data-testid={`user-role-${u.id}`} className="bg-surface border border-white/10 rounded-sm px-2 py-1.5 text-white/80 outline-none focus:border-gold/60">{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
                  </td>
                  <td className="py-3 px-3">
                    <select value={u.plan} onChange={(e) => updUser(u.id, { plan: e.target.value })} data-testid={`user-plan-${u.id}`} className="bg-surface border border-white/10 rounded-sm px-2 py-1.5 text-white/80 outline-none focus:border-gold/60">{PLANS.map((r) => <option key={r} value={r}>{r}</option>)}</select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EditNum({ value, onSave, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return <input type="number" value={v} data-testid={testid} onChange={(e) => setV(+e.target.value)} onBlur={() => v !== value && onSave(v)}
    className="w-24 bg-surface border border-white/10 rounded-sm px-2 py-1.5 text-white/80 font-mono outline-none focus:border-gold/60" />;
}
